/**
 * Course management service for Firebase
 * Handles CRUD operations for courses, modules, and progress tracking
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  setDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  getAggregateFromServer,
  sum as aggSum,
  Timestamp,
  increment,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from './config';
import { awardXP } from './gamification';
import {
  Course,
  Module,
  UserProgress,
  UserFavorite,
  CourseFilters,
  ProgressStatus,
  CourseReviewFeedback,
} from '../../types/course';

/**
 * Course service class
 */
export class CourseService {
  /**
   * Get global stats for courses (counts and total enrollments)
   * Optionally scoped to a creator (for instructor views)
   */
  async getCourseStats(options?: { createdBy?: string }): Promise<{
    total: number;
    published: number;
    draft: number;
    totalEnrollments: number;
  }> {
    try {
      const base = collection(db, 'courses');
      const filters = options?.createdBy ? [where('createdBy', '==', options.createdBy)] : [];

      // Total count
      const totalQ = query(base, ...filters);
      const totalSnap = await getCountFromServer(totalQ);
      const total = totalSnap.data().count;

      // Published count
      const pubQ = query(base, where('isPublished', '==', true), ...filters);
      const pubSnap = await getCountFromServer(pubQ);
      const published = pubSnap.data().count;

      // Draft count
      const draftQ = query(base, where('isPublished', '==', false), ...filters);
      const draftSnap = await getCountFromServer(draftQ);
      const draft = draftSnap.data().count;

      // Total enrollments sum (aggregate)
      // Use alias to access value from snapshot
      let totalEnrollments = 0;
      try {
        const enrollSnap = await getAggregateFromServer(query(base, ...filters), {
          enrollmentSum: aggSum('enrollmentCount') as any,
        } as any);
        const data: any = enrollSnap.data();
        totalEnrollments = (data && (data.enrollmentSum ?? data.sum)) || 0;
      } catch (_) {
        // Fallback: approximate by fetching a single page (lightweight) if aggregate not supported
        const q = query(base, ...filters, orderBy('createdAt', 'desc'), limit(500));
        const snap = await getDocs(q);
        totalEnrollments = snap.docs.reduce((acc, d) => acc + (d.data().enrollmentCount || 0), 0);
      }

      return { total, published, draft, totalEnrollments };
    } catch (error) {
      return { total: 0, published: 0, draft: 0, totalEnrollments: 0 };
    }
  }
  /**
   * Delete a course, its modules, and all related storage files
   */
  async deleteCourse(courseId: string): Promise<void> {
    try {
      // Get course
      const course = await this.getCourse(courseId);
      if (!course) return;

      // Delete course-level storage files
      const { storage } = await import('./config');
      const storageFiles: string[] = [];
      if (course.thumbnail) storageFiles.push(course.thumbnail);
      if (course.coverImage) storageFiles.push(course.coverImage);

      // Get all modules for the course
      const modules = await this.getCourseModules(courseId);
      for (const module of modules) {
        // Delete module storage files
        if (module.content) {
          const { videoUrl, audioUrl, pdfUrl } = module.content;
          if (videoUrl) storageFiles.push(videoUrl);
          if (audioUrl) storageFiles.push(audioUrl);
          if (pdfUrl) storageFiles.push(pdfUrl);
        }
        // Delete module document
        await this.deleteModule(module.id);
      }

      // Delete course document
      await deleteDoc(doc(db, 'courses', courseId));

      // Delete files from Firebase Storage
      for (const fileUrl of storageFiles) {
        try {
          // Only delete if fileUrl is a gs:// or https:// URL
          if (fileUrl.startsWith('gs://') || fileUrl.includes('/o/')) {
            // Get storage ref from URL
            const { ref, deleteObject } = await import('firebase/storage');
            let storageRef;
            if (fileUrl.startsWith('gs://')) {
              storageRef = ref(storage, fileUrl);
            } else {
              // Parse path from https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<path>
              const match = fileUrl.match(/\/o\/([^?]+)/);
              if (match) {
                const path = decodeURIComponent(match[1]);
                storageRef = ref(storage, path);
              }
            }
            if (storageRef) {
              await deleteObject(storageRef);
            }
          }
        } catch (err) {
          // Ignore storage deletion errors
        }
      }
    } catch (error) {
      throw error;
    }
  }
  private static instance: CourseService;
  
  public static getInstance(): CourseService {
    if (!CourseService.instance) {
      CourseService.instance = new CourseService();
    }
    return CourseService.instance;
  }

  /**
   * Get all published courses with optional filtering
   */
  async getCourses(
    filters?: CourseFilters,
    pagination?: { pageSize?: number; startAfterDoc?: any }
  ): Promise<{ courses: Course[]; hasMore: boolean; lastDoc: any }> {
    try {
      // If called from course management, allow fetching all courses
      // If filters.status is set, filter by isPublished accordingly
      let q;
      if (filters?.status === 'draft') {
        q = query(
          collection(db, 'courses'),
          where('isPublished', '==', false),
          orderBy('createdAt', 'desc')
        );
      } else if (filters?.status === 'published') {
        q = query(
          collection(db, 'courses'),
          where('isPublished', '==', true),
          orderBy('createdAt', 'desc')
        );
      } else {
        // No status filter: fetch all courses
        q = query(
          collection(db, 'courses'),
          orderBy('createdAt', 'desc')
        );
      }

      // Apply filters
      if (filters?.category) {
        q = query(q, where('category', '==', filters.category));
      }
      
      if (filters?.difficulty) {
        q = query(q, where('difficulty', '==', filters.difficulty));
      }
      


      // Pagination
  const pageLimit = pagination?.pageSize ?? 50;
      q = query(q, limit(pageLimit + 1));
      if (pagination?.startAfterDoc) {
        q = query(q, startAfter(pagination.startAfterDoc));
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      const hasMore = docs.length > pageLimit;
      const pageDocs = hasMore ? docs.slice(0, pageLimit) : docs;
      const courses: Course[] = [];
      
      pageDocs.forEach((doc) => {
        const data = doc.data();
        courses.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as Course);
      });

      // Filter by search term (client-side for now)
      let filteredCourses = courses;
      if (filters?.searchTerm) {
        const searchTerm = filters.searchTerm.toLowerCase();
        filteredCourses = courses.filter(course => {
          const title = (course.title || '').toLowerCase();
          const desc = (course.description || '').toLowerCase();
          const shortDesc = (course.shortDescription || '').toLowerCase();
          const tags = Array.isArray(course.tags) ? course.tags : [];
          return (
            title.includes(searchTerm) ||
            desc.includes(searchTerm) ||
            shortDesc.includes(searchTerm) ||
            tags.some(tag => (tag || '').toLowerCase().includes(searchTerm))
          );
        });
      }

      // Filter by favorites (if user is authenticated)
      if (filters?.showFavoritesOnly && auth.currentUser) {
        const userFavorites = await this.getUserFavorites();
        const favoriteIds = new Set(userFavorites.map(fav => fav.courseId));
        filteredCourses = filteredCourses.filter(course => favoriteIds.has(course.id));
      }

      // Filter by free courses (client-side to avoid index requirements)
      if (filters?.isFree !== undefined) {
        filteredCourses = filteredCourses.filter(course => {
          const tier = course.requiredSubscriptionTier || 'free';
          if (filters.isFree) {
            return tier === 'free';
          } else {
            return tier !== 'free';
          }
        });
      }

      return {
        courses: filteredCourses,
        hasMore,
        // Use the last doc of the returned page as the cursor for the next page
        lastDoc: pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null,
      };
    } catch (error) {
      return { courses: [], hasMore: false, lastDoc: null };
    }
  }

  /**
   * Get a single course by ID
   */
  async getCourse(courseId: string): Promise<Course | null> {
    try {
      const courseDoc = await getDoc(doc(db, 'courses', courseId));
      if (!courseDoc.exists()) {
        return null;
      }

      const data = courseDoc.data();
      return {
        id: courseDoc.id,
        ...data,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as Course;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get modules for a course
   */
  async getCourseModules(courseId: string): Promise<Module[]> {
    try {
      const q = query(
        collection(db, 'modules'),
        where('courseId', '==', courseId),
        orderBy('order', 'asc')
      );

      const snapshot = await getDocs(q);
      const modules: Module[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        modules.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
        } as Module);
      });

      return modules;
    } catch (error) {
      return [];
    }
  }

  /**
   * Get a single module by ID
   */
  async getModule(moduleId: string): Promise<Module | null> {
    try {
      const moduleDoc = await getDoc(doc(db, 'modules', moduleId));
      if (!moduleDoc.exists()) {
        return null;
      }

      const data = moduleDoc.data();
      return {
        id: moduleDoc.id,
        ...data,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as Module;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create a new course
   */
  async createCourse(courseData: Omit<Course, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const payload = {
        ...courseData,
        createdAt: now,
        updatedAt: now,
      };
      const docRef = await addDoc(collection(db, 'courses'), payload);
      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Set a course (create or overwrite)
   */
  async setCourse(courseId: string, courseData: Course): Promise<void> {
    try {
      await setDoc(doc(db, 'courses', courseId), {
        ...courseData,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a course
   */
  async updateCourse(courseId: string, updates: Partial<Course>): Promise<void> {
    try {
      await updateDoc(doc(db, 'courses', courseId), {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create a new module
   */
  async createModule(moduleData: Omit<Module, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const payload = {
        ...moduleData,
        createdAt: now,
        updatedAt: now,
      };
      const docRef = await addDoc(collection(db, 'modules'), payload);

      // Update course's moduleIds array
      const course = await this.getCourse(moduleData.courseId);
      if (course) {
        const updatedModuleIds = [...course.moduleIds, docRef.id];
        await this.updateCourse(moduleData.courseId, {
          moduleIds: updatedModuleIds,
          totalModules: updatedModuleIds.length,
        });
      }

      return docRef.id;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update a module
   */
  async updateModule(moduleId: string, updates: Partial<Module>): Promise<void> {
    try {
      await updateDoc(doc(db, 'modules', moduleId), {
        ...updates,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a module
   */
  async deleteModule(moduleId: string): Promise<void> {
    try {
      const module = await this.getModule(moduleId);
      if (!module) return;

      await deleteDoc(doc(db, 'modules', moduleId));

      // Update course's moduleIds array
      const course = await this.getCourse(module.courseId);
      if (course) {
        const updatedModuleIds = course.moduleIds.filter(id => id !== moduleId);
        await this.updateCourse(module.courseId, {
          moduleIds: updatedModuleIds,
          totalModules: updatedModuleIds.length,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user progress for a course
   */
  async getUserProgress(courseId: string): Promise<UserProgress | null> {
    try {
      if (!auth.currentUser) return null;
      const userId = auth.currentUser.uid;
      const docId = `${userId}_${courseId}`;
      const progressRef = doc(db, 'user_progress', docId);
      const progressDoc = await getDoc(progressRef);
      if (!progressDoc.exists()) return null;
      const data = progressDoc.data();
      
      // Recalculate completion percentage based on actual module count
      const modulesQuery = query(
        collection(db, 'modules'),
        where('courseId', '==', courseId)
      );
      const modulesSnapshot = await getDocs(modulesQuery);
      const actualModuleCount = modulesSnapshot.size;
      
      if (actualModuleCount > 0) {
        const completedCount = data.moduleProgress?.filter((mp: any) => mp.status === 'completed').length || 0;
        const correctTotalModules = actualModuleCount;
        let correctCompletionPercentage = (completedCount / correctTotalModules) * 100;
        if (correctCompletionPercentage > 100) correctCompletionPercentage = 100;
        
        // If the stored values are incorrect, update them
        if (data.totalModules !== correctTotalModules || Math.abs(data.completionPercentage - correctCompletionPercentage) > 0.1) {
          const updatePayload: any = {
            totalModules: correctTotalModules,
            completionPercentage: correctCompletionPercentage,
            completedModules: completedCount,
            updatedAt: Timestamp.now()
          };
          
          // Mark as completed if 100%
          if (correctCompletionPercentage === 100 && !data.completedAt) {
            updatePayload.completedAt = Timestamp.now();
            
            // Award XP for course completion
            try {
              // Get course details to check if it's free or paid
              const courseRef = doc(db, 'courses', courseId);
              const courseSnap = await getDoc(courseRef);
              
              if (courseSnap.exists()) {
                const courseData = courseSnap.data();
                const isFree = courseData.price === 0 || courseData.price === '0' || courseData.isFree;
                
                // Award XP based on course type
                if (isFree) {
                  await awardXP(userId, 'complete_first_free_course', 50);
                } else {
                  await awardXP(userId, 'complete_first_paid_course', 20);
                }
                
                // Check for milestone achievements
                await this.checkAndAwardCourseMilestones(userId);
                
                // Dispatch gamification update event
                window.dispatchEvent(new CustomEvent('gamificationUpdate'));
              }
            } catch (xpError) {
              console.error('[Gamification] Error awarding course completion XP:', xpError);
            }
          }
          
          // Update the progress document
          await updateDoc(progressRef, updatePayload);
          
          // Update the returned data
          data.totalModules = correctTotalModules;
          data.completionPercentage = correctCompletionPercentage;
          data.completedModules = completedCount;
          if (updatePayload.completedAt) {
            data.completedAt = updatePayload.completedAt;
          }
          
          // Also update the course document if needed
          const courseRef = doc(db, 'courses', courseId);
          const courseSnap = await getDoc(courseRef);
          if (courseSnap.exists() && courseSnap.data().totalModules !== correctTotalModules) {
            await updateDoc(courseRef, { totalModules: correctTotalModules });
          }
        }
      }
      
      return {
        id: progressDoc.id,
        ...data,
        enrolledAt: data.enrolledAt,
        startedAt: data.startedAt,
        completedAt: data.completedAt,
        lastAccessedAt: data.lastAccessedAt,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as UserProgress;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create or update user progress
   */
  async updateUserProgress(progressData: Partial<UserProgress>): Promise<void> {
    try {
      if (!auth.currentUser) return;

      const userId = auth.currentUser.uid;
      const courseId = progressData.courseId!;
      const now = Timestamp.now();
      const docId = `${userId}_${courseId}`;
      const progressRef = doc(db, 'user_progress', docId);

      const existingProgress = await this.getUserProgress(courseId);

      if (existingProgress) {
        await updateDoc(progressRef, {
          ...progressData,
          lastAccessedAt: now,
          updatedAt: now,
        });
      } else {
        await setDoc(progressRef, {
          userId,
          ...progressData,
          enrolledAt: now,
          lastAccessedAt: now,
          createdAt: now,
          updatedAt: now,
        });
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Add course to favorites
   */
  async addFavorite(courseId: string): Promise<void> {
    try {
      if (!auth.currentUser) return;

      // Check if already favorited
      const q = query(
        collection(db, 'user_favorites'),
        where('userId', '==', auth.currentUser.uid),
        where('courseId', '==', courseId)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) return; // Already favorited

      await addDoc(collection(db, 'user_favorites'), {
        userId: auth.currentUser.uid,
        courseId,
        createdAt: Timestamp.now(),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Remove course from favorites
   */
  async removeFavorite(courseId: string): Promise<void> {
    try {
      if (!auth.currentUser) return;

      const q = query(
        collection(db, 'user_favorites'),
        where('userId', '==', auth.currentUser.uid),
        where('courseId', '==', courseId)
      );

      const snapshot = await getDocs(q);
      snapshot.docs.forEach(async (doc) => {
        await deleteDoc(doc.ref);
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get user's favorite courses
   */
  async getUserFavorites(): Promise<UserFavorite[]> {
    try {
      if (!auth.currentUser) return [];

      const q = query(
        collection(db, 'user_favorites'),
        where('userId', '==', auth.currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const favorites: UserFavorite[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        favorites.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt,
        } as UserFavorite);
      });

      return favorites;
    } catch (error) {
      return [];
    }
  }

  /**
   * Reorder modules in a course
   */
  async reorderModules(courseId: string, moduleIds: string[]): Promise<void> {
    try {
      // Update each module's order field
      const updatePromises = moduleIds.map((moduleId, index) => 
        updateDoc(doc(db, 'modules', moduleId), {
          order: index,
          updatedAt: Timestamp.now(),
        })
      );

      // Update course's moduleIds array
      const courseUpdatePromise = updateDoc(doc(db, 'courses', courseId), {
        moduleIds: moduleIds,
        updatedAt: Timestamp.now(),
      });

      // Execute all updates
      await Promise.all([...updatePromises, courseUpdatePromise]);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Insert a module at a specific position in a course
   */
  async insertModuleAtPosition(
    courseId: string, 
    moduleId: string, 
    position: number
  ): Promise<void> {
    try {
      // Get current course
      const course = await this.getCourse(courseId);
      if (!course) throw new Error('Course not found');

      // Insert module ID at the specified position
      const newModuleIds = [...course.moduleIds];
      newModuleIds.splice(position, 0, moduleId);

      // Update all module orders
      const updatePromises = newModuleIds.map((id, index) => 
        updateDoc(doc(db, 'modules', id), {
          order: index,
          updatedAt: Timestamp.now(),
        })
      );

      // Update course
      const courseUpdatePromise = updateDoc(doc(db, 'courses', courseId), {
        moduleIds: newModuleIds,
        totalModules: newModuleIds.length,
        updatedAt: Timestamp.now(),
      });

      // Execute all updates
      await Promise.all([...updatePromises, courseUpdatePromise]);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Normalize module orders to ensure they are sequential (0, 1, 2, ...)
   */
  async normalizeModuleOrders(courseId: string): Promise<void> {
    try {
      const modules = await this.getCourseModules(courseId);
      
      // Sort modules by current order
      modules.sort((a, b) => a.order - b.order);
      
      // Update orders to be sequential
      const updatePromises = modules.map((module, index) => 
        updateDoc(doc(db, 'modules', module.id), {
          order: index,
          updatedAt: Timestamp.now(),
        })
      );

      // Update course moduleIds to match the normalized order
      const normalizedModuleIds = modules.map(m => m.id);
      const courseUpdatePromise = updateDoc(doc(db, 'courses', courseId), {
        moduleIds: normalizedModuleIds,
        updatedAt: Timestamp.now(),
      });

      await Promise.all([...updatePromises, courseUpdatePromise]);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Enroll user in a course
   */
  async enrollInCourse(courseId: string): Promise<void> {
    try {
      if (!auth.currentUser) return;

      const course = await this.getCourse(courseId);
      if (!course) throw new Error('Course not found');

      // Get user data to check credits for twelve_month_package users
      const userRef = doc(db, 'users', auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const isTwelveMonthUser = userData.subscriptionTier === 'twelve_month_package';
        const currentCredits = userData.credits || 0;
        
        // Check if this is a paid course that requires credits
        const isPaidCourse = !course.isFree && course.requiredSubscriptionTier !== 'free';
        
        if (isTwelveMonthUser && isPaidCourse) {
          // Twelve-month users need credits to enroll in paid courses
          if (currentCredits < 1) {
            throw new Error('Insufficient credits. You need 1 credit to enroll in this course.');
          }
          
          // Deduct 1 credit
          await updateDoc(userRef, {
            credits: increment(-1),
            updatedAt: serverTimestamp(),
          });
          // ...existing code...
        }
      }

      // Create initial progress record
      await this.updateUserProgress({
        courseId,
        status: ProgressStatus.NOT_STARTED,
        moduleProgress: [],
        completedModules: 0,
        totalModules: course.totalModules,
        completionPercentage: 0,
        totalTimeSpent: 0,
      });

      // Increment enrollment count
      await updateDoc(doc(db, 'courses', courseId), {
        enrollmentCount: increment(1),
      });
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check and award milestone XP for course completions
   */
  private async checkAndAwardCourseMilestones(userId: string): Promise<void> {
    try {
      // Query all completed courses for this user
      const progressQuery = query(
        collection(db, 'userProgress'),
        where('userId', '==', userId),
        where('completionPercentage', '==', 100)
      );
      
      const progressSnap = await getDocs(progressQuery);
      const completedCourses = progressSnap.docs;
      
      // Count total completed courses
      const totalCompleted = completedCourses.length;
      
      // Count completed paid courses
      let paidCoursesCompleted = 0;
      
      for (const progressDoc of completedCourses) {
        const courseId = progressDoc.data().courseId;
        const courseRef = doc(db, 'courses', courseId);
        const courseSnap = await getDoc(courseRef);
        
        if (courseSnap.exists()) {
          const courseData = courseSnap.data();
          const isFree = courseData.price === 0 || courseData.price === '0' || courseData.isFree;
          if (!isFree) {
            paidCoursesCompleted++;
          }
        }
      }
      
      // Check and award milestone XP
      if (paidCoursesCompleted >= 5) {
        await awardXP(userId, 'complete_5_paid_courses', 100);
      }
      
      if (totalCompleted >= 10) {
        await awardXP(userId, 'finish_10_courses', 100);
      }
      
    } catch (error) {
      console.error('[Gamification] Error checking course milestones:', error);
    }
  }

  /**
   * Get the first uncompleted module for a course
   * Returns the first module that is not completed, or the first module if none are completed
   */
  async getFirstUncompletedModule(courseId: string): Promise<Module | null> {
    try {
      // Get all modules for the course
      const modules = await this.getCourseModules(courseId);
      if (modules.length === 0) return null;

      // Get user progress
      const progress = await this.getUserProgress(courseId);
      
      // If no progress, return first module
      if (!progress || !progress.moduleProgress || progress.moduleProgress.length === 0) {
        return modules[0];
      }

      // Find first module that is not completed
      for (const module of modules) {
        const moduleProgress = progress.moduleProgress.find(mp => mp.moduleId === module.id);
        if (!moduleProgress || moduleProgress.status !== ProgressStatus.COMPLETED) {
          return module;
        }
      }

      // All modules completed, return first module
      return modules[0];
    } catch (error) {
      console.error('Error getting first uncompleted module:', error);
      return null;
    }
  }

  /**
   * Reset course progress for a user (restart course)
   * Clears all progress and allows the user to start from the beginning
   */
  async resetCourseProgress(courseId: string): Promise<void> {
    try {
      if (!auth.currentUser) return;
      const userId = auth.currentUser.uid;
      const docId = `${userId}_${courseId}`;
      const progressRef = doc(db, 'user_progress', docId);
      
      // Delete the progress document
      await deleteDoc(progressRef);

    } catch (error) {
      console.error('Error resetting course progress:', error);
      throw error;
    }
  }

  /**
   * Get all completed courses for the current user
   */
  async getCompletedCourses(): Promise<Course[]> {
    try {
      if (!auth.currentUser) return [];
      const userId = auth.currentUser.uid;

      const progressSnapshot = await getDocs(
        query(collection(db, 'user_progress'), where('userId', '==', userId))
      );

      if (progressSnapshot.empty) {
        return [];
      }

      const completedProgress = progressSnapshot.docs
        .map(doc => doc.data() as UserProgress)
        .filter(progress => {
          if (progress.completedAt) {
            return true;
          }

          const completion = Math.min(100, Math.max(0, progress.completionPercentage ?? 0));
          return completion >= 100 || progress.status === ProgressStatus.COMPLETED;
        })
        .sort((a, b) => {
          const getDateValue = (item: UserProgress) => {
            const timestamp = item.completedAt || item.updatedAt || item.lastAccessedAt || item.enrolledAt;
            return timestamp ? timestamp.toMillis() : 0;
          };

          return getDateValue(b) - getDateValue(a);
        });

      if (completedProgress.length === 0) {
        return [];
      }

      const coursePromises = completedProgress.map(progress => this.getCourse(progress.courseId));
      const courses = await Promise.all(coursePromises);

      return courses.filter(course => course !== null) as Course[];
    } catch (error) {
      console.error('Error getting completed courses:', error);
      return [];
    }
  }

  /**
   * Get completed courses with their completion dates for dashboard display.
   * Same data source as getCompletedCourses, but also returns the completedAt
   * timestamp from user_progress for accurate date display.
   */
  async getCompletedCoursesWithDates(): Promise<Array<{ course: Course; completedAt: Timestamp | null }>> {
    try {
      if (!auth.currentUser) return [];
      const userId = auth.currentUser.uid;

      const progressSnapshot = await getDocs(
        query(collection(db, 'user_progress'), where('userId', '==', userId))
      );

      if (progressSnapshot.empty) return [];

      const completedProgress = progressSnapshot.docs
        .map(doc => doc.data() as UserProgress)
        .filter(progress => {
          if (progress.completedAt) return true;
          const completion = Math.min(100, Math.max(0, progress.completionPercentage ?? 0));
          return completion >= 100 || progress.status === ProgressStatus.COMPLETED;
        })
        .sort((a, b) => {
          const getDateValue = (item: UserProgress) => {
            const timestamp = item.completedAt || item.updatedAt || item.lastAccessedAt || item.enrolledAt;
            return timestamp ? timestamp.toMillis() : 0;
          };
          return getDateValue(b) - getDateValue(a);
        });

      if (completedProgress.length === 0) return [];

      const results = await Promise.all(
        completedProgress.map(async progress => ({
          course: await this.getCourse(progress.courseId),
          completedAt: progress.completedAt ?? null,
        }))
      );

      return results.filter(r => r.course !== null) as Array<{ course: Course; completedAt: Timestamp | null }>;
    } catch (error) {
      console.error('Error getting completed courses with dates:', error);
      return [];
    }
  }

  /**
   * Submit course review feedback
   * Creates a review document in Firestore and updates course rating
   */
  async submitCourseReview(
    courseId: string,
    feedback: Omit<CourseReviewFeedback, 'id' | 'courseId' | 'userId' | 'createdAt' | 'updatedAt'>
  ): Promise<void> {
    try {
      if (!auth.currentUser) {
        throw new Error('User must be authenticated to submit review');
      }

      const userId = auth.currentUser.uid;

      // Check if user has already submitted a review
      const existingReviewQuery = query(
        collection(db, 'course_reviews'),
        where('courseId', '==', courseId),
        where('userId', '==', userId)
      );
      const existingReviewSnapshot = await getDocs(existingReviewQuery);

      if (!existingReviewSnapshot.empty) {
        // Update existing review
        const reviewDoc = existingReviewSnapshot.docs[0];
        await updateDoc(doc(db, 'course_reviews', reviewDoc.id), {
          ...feedback,
          updatedAt: serverTimestamp(),
        });
      } else {
        // Create new review
        await addDoc(collection(db, 'course_reviews'), {
          courseId,
          userId,
          ...feedback,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }

      // Update course average rating
      await this.updateCourseAverageRating(courseId);

      // eslint-disable-next-line no-console
      console.log('[CourseService] Course review submitted successfully');
    } catch (error) {
      console.error('[CourseService] Error submitting course review:', error);
      throw error;
    }
  }

  /**
   * Get course review feedback for a specific user
   */
  async getCourseReview(courseId: string, userId?: string): Promise<CourseReviewFeedback | null> {
    try {
      const targetUserId = userId || auth.currentUser?.uid;
      if (!targetUserId) return null;

      const reviewQuery = query(
        collection(db, 'course_reviews'),
        where('courseId', '==', courseId),
        where('userId', '==', targetUserId)
      );

      const snapshot = await getDocs(reviewQuery);
      if (snapshot.empty) return null;

      const reviewDoc = snapshot.docs[0];
      return {
        id: reviewDoc.id,
        ...reviewDoc.data(),
      } as CourseReviewFeedback;
    } catch (error) {
      console.error('[CourseService] Error fetching course review:', error);
      return null;
    }
  }

  /**
   * Update course average rating based on all reviews
   */
  private async updateCourseAverageRating(courseId: string): Promise<void> {
    try {
      const reviewsQuery = query(
        collection(db, 'course_reviews'),
        where('courseId', '==', courseId)
      );
      const snapshot = await getDocs(reviewsQuery);

      if (snapshot.empty) return;

      let totalRating = 0;
      let count = 0;

      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.overallRating) {
          totalRating += data.overallRating;
          count++;
        }
      });

      const averageRating = count > 0 ? totalRating / count : 0;

      // Update course document (requires instructor/admin permissions)
      try {
        const courseRef = doc(db, 'courses', courseId);
        await updateDoc(courseRef, {
          averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
          reviewCount: count,
          updatedAt: serverTimestamp(),
        });

        // eslint-disable-next-line no-console
        console.log(`[CourseService] Updated course ${courseId} rating: ${averageRating} (${count} reviews)`);
      } catch (courseUpdateError) {
        // Silently fail if user doesn't have permission - course rating updates should be done via Cloud Function
        // The review itself is still saved successfully in course_reviews collection
      }
    } catch (error) {
      console.error('[CourseService] Error updating course average rating:', error);
    }
  }

  /**
   * Check if user has completed course review
   */
  async hasCompletedReview(courseId: string, userId?: string): Promise<boolean> {
    try {
      const review = await this.getCourseReview(courseId, userId);
      return review !== null;
    } catch (error) {
      console.error('[CourseService] Error checking review completion:', error);
      return false;
    }
  }

  /**
   * Extract topics/skills from a course using AI
   * This calls a Cloud Function that analyzes the course content
   */
  async extractCourseTopics(courseId: string): Promise<string[]> {
    try {
      // Get Firebase Functions instance
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const extractTopics = httpsCallable<{ courseId: string }, { topics: string[] }>(
        functions,
        'extractCourseTopics'
      );

      const result = await extractTopics({ courseId });
      return result.data.topics;
    } catch (error) {
      console.error('[CourseService] Error extracting course topics:', error);
      // Return generic fallback topics if extraction fails
      return [
        'Understanding Concepts',
        'Practical Skills',
        'Tool Usage',
        'Real-world Application',
        'Best Practices',
        'Problem Solving'
      ];
    }
  }
}

// Export singleton instance
export const courseService = CourseService.getInstance();



/**
 * Get user learning statistics
 */
export async function getUserLearningStats(userId: string): Promise<{
  coursesCompleted: number;
  coursesInProgress: number;
  totalLearningTime: number;
  certificatesEarned: number;
}> {
  try {
    const progressQuery = query(
      collection(db, 'user_progress'),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(progressQuery);
    
    let coursesCompleted = 0;
    let coursesInProgress = 0;
    let totalLearningTime = 0;
    let certificatesEarned = 0;
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      if (data.status === 'completed') {
        coursesCompleted++;
        certificatesEarned++; // Assuming each completed course earns a certificate
      } else if (data.status === 'in_progress') {
        coursesInProgress++;
      }
      
      // Sum up total time spent (in hours)
      if (data.totalTimeSpent) {
        totalLearningTime += data.totalTimeSpent;
      }
    });
    
    return {
      coursesCompleted,
      coursesInProgress,
      totalLearningTime: Math.round(totalLearningTime),
      certificatesEarned,
    };
  } catch (error) {
    console.error('Error getting user learning stats:', error);
    return {
      coursesCompleted: 0,
      coursesInProgress: 0,
      totalLearningTime: 0,
      certificatesEarned: 0,
    };
  }
}

